 import React, { useRef, useEffect, useState } from 'react';

interface TestimonialsType {
    name: string;
    avatar: string;
    star: number;
    review: string;
}

interface TestimonialsCarouselProps {
    testimonials: TestimonialsType[];
}

const TestimonialsCarousel: React.FC<TestimonialsCarouselProps> = ({ testimonials }) => {
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.scrollWidth);
        }
    }, [testimonials]);

    // Function to render stars based on rating
    const renderStars = (rating: number) => {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            stars.push(
                <svg 
                    key={i} 
                    className={`w-5 h-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            );
        }
        return stars;
    };

    // Render a single testimonial card
    const renderTestimonialCard = (testimonial: TestimonialsType, key: string | number) => (
        <div key={key} className="flex-shrink-0 w-80 md:w-96 bg-white rounded-lg shadow-lg p-6 mx-4 my-2">
            <div className="flex items-center mb-4">
                {testimonial.avatar && (
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                        <img 
                            src={testimonial.avatar} 
                            alt={`${testimonial.name}'s avatar`} 
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                <div>
                    <h3 className="font-bold text-lg">{testimonial.name}</h3>
                    <div className="flex mt-1">
                        {renderStars(testimonial.star)}
                    </div>
                </div>
            </div>
            <p className="text-gray-700 text-sm md:text-base line-clamp-6">
                {testimonial.review}
            </p>
            {testimonial.review.length > 200 && (
                <button className="mt-2 text-blue-600 text-sm font-medium">
                    Read more
                </button>
            )}
        </div>
    );

    return (
        <div className="w-full overflow-hidden my-12">
            {/* Animated carousel for all screen sizes */}
            <div className="relative" ref={containerRef}>
                <div
                    className="flex animate-marquee"
                    style={{
                        width: `${containerWidth}px`,
                    }}
                >
                    {testimonials.map((testimonial, index) => (
                        renderTestimonialCard(testimonial, index)
                    ))}
                    {testimonials.map((testimonial, index) => (
                        renderTestimonialCard(testimonial, `repeat-${index}`)
                    ))}
                </div>
            </div>
        </div>
    );
};

export default TestimonialsCarousel;